import React, { useState, useEffect } from 'react';
import { Mail, Sparkles, Wand2, ChevronDown, ChevronUp, RotateCcw } from 'lucide-react';
import FormModal from './FormModal';


interface EmailTemplate {
  id: number;
  template_key: string;
  name: string;
  category: 'system' | 'compliance' | 'expiry' | 'notification' | 'marketing' | 'custom';
  subject: string;
  html_content: string;
  text_content: string | null;
  variables: string[];
  required_variables: string[] | null;
  is_active: boolean;
  is_system: boolean;
  is_editable: boolean;
  usage_count: number;
  last_used_at: string | null;
  created_at: string;
  updated_at: string;
}

interface EmailTrigger {
  id: number;
  name: string;
  type: string;
  event_type: string;
  template_id?: number;
  is_active: boolean;
}

interface EmailTemplateModalProps {
  onClose: () => void;
  onSubmit: (template: Partial<EmailTemplate>) => Promise<void> | void;
  selectedTemplate: EmailTemplate | null;
}

export const EmailTemplateModal: React.FC<EmailTemplateModalProps> = ({
  onClose,
  onSubmit,
  selectedTemplate,
}) => {
  const [contentMode, setContentMode] = useState<'html' | 'text'>('html');
  const [allTriggers, setAllTriggers] = useState<EmailTrigger[]>([]);
  const [selectedTriggerId, setSelectedTriggerId] = useState<number | ''>('');
  const [loadingTriggers, setLoadingTriggers] = useState(false);

  // AI assist state
  const [showAI, setShowAI] = useState(false);
  const [aiPrompt, setAIPrompt] = useState('');
  const [isAILoading, setIsAILoading] = useState(false);

  const callTemplateAI = async (instruction: string) => {
    setIsAILoading(true);
    try {
      const categoryLabel = template.category || 'custom';
      const existingContent = (contentMode === 'html' ? template.html_content : template.text_content) || '';
      const existing = existingContent ? `\n\nExisting draft:\nSubject: ${template.subject || ''}\n${existingContent}` : '';
      const outputFormat = contentMode === 'html' ? 'simple inline-styled HTML (no <html>/<head>/<body> tags)' : 'plain text';

      const prompt = `Write a professional ${categoryLabel} email template for a compliance platform. Task: ${instruction}${existing}

Reply ONLY with this JSON (no markdown fences):
{"subject":"<subject line with {{variable}} placeholders>","content":"<${outputFormat} with {{variable}} placeholders like {{first_name}}, {{company_name}}>"}`;

      const response = await fetch('/api/ai/orchestrate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ user_message: prompt }),
      });
      if (!response.ok) throw new Error('failed');
      const data = await response.json();
      const text: string = data.message || data.response || data.answer || '';

      let parsed: { subject?: string; content?: string } | null = null;
      try { parsed = JSON.parse(text); } catch {}
      if (!parsed) { try { parsed = JSON.parse(text.replace(/```json|```/g, '').trim()); } catch {} }
      if (!parsed) { const m = text.match(/\{[\s\S]*?\}/); if (m) { try { parsed = JSON.parse(m[0]); } catch {} } }
      if (!parsed && text.length > 10) {
        parsed = { subject: template.subject || text.split('\n')[0]?.slice(0, 80), content: text };
      }

      if (parsed && (parsed.subject || parsed.content)) {
        setTemplate(prev => ({
          ...prev,
          ...(parsed!.subject ? { subject: parsed!.subject } : {}),
          ...(contentMode === 'html' && parsed!.content ? { html_content: parsed!.content } : {}),
          ...(contentMode === 'text' && parsed!.content ? { text_content: parsed!.content } : {}),
        }));
        setAIPrompt('');
        setShowAI(false);
        // Auto-detect variables from content
        const vars = [...(parsed.subject || ''), ...(parsed.content || '')].join(' ').match(/\{\{(\w+)\}\}/g);
        if (vars) {
          const uniqueVars = [...new Set(vars.map(v => v.replace(/\{\{|\}\}/g, '')))];
          setTemplate(prev => ({ ...prev, variables: uniqueVars }));
        }
      } else {
        alert('Could not generate template. Try rephrasing your request.');
      }
    } catch {
      alert('AI assist failed. Please try again.');
    } finally {
      setIsAILoading(false);
    }
  };

  const [template, setTemplate] = useState<Partial<EmailTemplate>>(
    selectedTemplate || {
      template_key: '',
      name: '',
      category: 'custom',
      subject: '',
      html_content: '',
      text_content: '',
      variables: [],
      required_variables: null,
      is_active: true,
      is_system: false,
      is_editable: true,
    }
  );

  const [loading, _setLoading] = useState(false);

  // Update state when selectedTemplate changes
  useEffect(() => {
    if (selectedTemplate) {
      setTemplate(selectedTemplate);
    }
  }, [selectedTemplate]);

  // Fetch triggers on component mount
  useEffect(() => {
    fetchAllTriggers();
  }, []);



  const fetchAllTriggers = async () => {
    setLoadingTriggers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/admin/email-triggers', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAllTriggers(data.data || []);
        // Set selected trigger ID based on current template usage
        if (selectedTemplate?.id) {
          const usingTrigger = data.data?.find((trigger: EmailTrigger) => trigger.template_id === selectedTemplate.id);
          setSelectedTriggerId(usingTrigger?.id || '');
        }
      }
    } catch (error) {
      console.error('Failed to fetch triggers:', error);
    } finally {
      setLoadingTriggers(false);
    }
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(template);

      // Update selected trigger to use this template
      if (selectedTriggerId) {
        try {
          await fetch(`/api/admin/email-triggers/${selectedTriggerId}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ template_id: template.id }),
          });
        } catch (error) {
          console.error('Failed to update trigger association:', error);
        }
      }

      onClose();
    } catch {
      // error already handled by parent; keep modal open
    }
  };

  const isCreate = !selectedTemplate?.id;

  return (
    <FormModal
      title={isCreate ? 'Create Email Template' : 'Edit Email Template'}
      subtitle={isCreate ? 'Create new email template' : 'Modify existing email template'}
      icon={<Mail className="text-white" size={24} />}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create Template' : 'Update Template'}
      cancelText="Cancel"
      loading={loading}
      size="large"
    >
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Associated Trigger</label>
            {loadingTriggers ? (
              <div className="text-sm text-slate-500">Loading triggers...</div>
            ) : (
              <select
                value={selectedTriggerId}
                onChange={(e) => setSelectedTriggerId(e.target.value ? parseInt(e.target.value) : '')}
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
                disabled={loading}
              >
                <option value="">Select a trigger...</option>
                {allTriggers.map(trigger => (
                  <option key={trigger.id} value={trigger.id.toString()}>
                    {trigger.name} - {trigger.event_type.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-slate-500">Select the trigger that should use this template</p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Name *</label>
            <input
              type="text"
              value={template.name || ''}
              onChange={(e) => setTemplate({ ...template, name: e.target.value })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              placeholder="Template display name"
              disabled={loading}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Category</label>
            <select
              value={template.category || 'custom'}
              onChange={(e) => setTemplate({ ...template, category: e.target.value as any })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
              disabled={loading}
            >
              <option value="system">System</option>
              <option value="compliance">Compliance</option>
              <option value="expiry">Expiry</option>
              <option value="notification">Notification</option>
              <option value="marketing">Marketing</option>
              <option value="custom">Custom</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Active</label>
            <div className="flex items-center mt-3">
              <input
                type="checkbox"
                checked={template.is_active || false}
                onChange={(e) => setTemplate({ ...template, is_active: e.target.checked })}
                className="h-5 w-5 text-slate-600 rounded focus:ring-2 focus:ring-slate-600 focus:ring-offset-0"
                disabled={loading}
              />
              <span className="ml-3 text-sm text-slate-700">Make this template active</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Subject Line *</label>
          <input
            type="text"
            value={template.subject || ''}
            onChange={(e) => setTemplate({ ...template, subject: e.target.value })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="Email subject line"
            disabled={loading}
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-slate-700">
              {contentMode === 'html' ? 'HTML Content *' : 'Text Content *'}
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowAI(v => !v)}
                className="flex items-center gap-1.5 text-xs font-medium text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1 rounded-full transition-colors"
              >
                <Sparkles className="w-3.5 h-3.5" />
                AI Assist
                {showAI ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
              <div className="flex bg-slate-100 rounded-lg p-1">
                <button
                  type="button"
                  onClick={() => setContentMode('html')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    contentMode === 'html'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  HTML
                </button>
                <button
                  type="button"
                  onClick={() => setContentMode('text')}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    contentMode === 'text'
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Text
                </button>
              </div>
            </div>
          </div>

          {/* AI Assist Panel */}
          {showAI && (
            <div className="border border-indigo-200 rounded-xl bg-indigo-50 p-3 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-indigo-700">
                <Wand2 className="w-4 h-4" />
                AI Template Generator
              </div>
              <div className="flex flex-wrap gap-2">
                {[
                  'Welcome email for new platform users',
                  'Compliance deadline reminder',
                  'Evidence submission request for audit',
                  'Framework update notification',
                  'Password reset / account security email',
                  'Monthly compliance summary report email',
                  'Evidence expiry warning',
                ].map(s => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => callTemplateAI(s)}
                    disabled={isAILoading}
                    className="text-xs bg-white border border-indigo-200 text-indigo-700 px-2.5 py-1 rounded-full hover:bg-indigo-100 transition-colors disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={aiPrompt}
                  onChange={(e) => setAIPrompt(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && aiPrompt.trim()) callTemplateAI(aiPrompt); }}
                  placeholder="Describe the email template you want to generate..."
                  className="flex-1 text-sm px-3 py-1.5 border border-indigo-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                  disabled={isAILoading}
                />
                <button
                  type="button"
                  onClick={() => aiPrompt.trim() && callTemplateAI(aiPrompt)}
                  disabled={isAILoading || !aiPrompt.trim()}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {isAILoading ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <Sparkles className="w-3.5 h-3.5" />}
                  Generate
                </button>
              </div>
              {(template.html_content || template.text_content) && (
                <button
                  type="button"
                  onClick={() => callTemplateAI('Rewrite and improve this email template to be more professional, branded, and effective')}
                  disabled={isAILoading}
                  className="flex items-center gap-1.5 text-xs text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                >
                  <RotateCcw className="w-3 h-3" />
                  Rewrite existing content
                </button>
              )}
            </div>
          )}

          <textarea
            value={contentMode === 'html' ? (template.html_content || '') : (template.text_content || '')}
            onChange={(e) => setTemplate({
              ...template,
              [contentMode === 'html' ? 'html_content' : 'text_content']: e.target.value
            })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all font-mono text-sm"
            rows={12}
            placeholder={contentMode === 'html' ? 'HTML email content' : 'Plain text email content'}
            disabled={loading}
          />
          <p className="text-xs text-slate-500">Use variables like &#123;&#123;user_name&#125;&#125;, &#123;&#123;company_name&#125;&#125;, etc.</p>
        </div>



        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">Variables (comma-separated)</label>
          <input
            type="text"
            value={template.variables?.join(', ') || ''}
            onChange={(e) => setTemplate({ ...template, variables: e.target.value.split(', ') })}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
            placeholder="user_name, company_name, expiry_date"
            disabled={loading}
          />
          <p className="text-xs text-slate-500">Variables available for use in the template content</p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-sm font-medium text-blue-900 mb-2">Available Variables</h4>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div>
              <strong className="text-blue-800">User Variables:</strong>
              <div className="text-blue-700 mt-1 space-y-1">
                <div><code>first_name</code> - User's first name</div>
                <div><code>last_name</code> - User's last name</div>
                <div><code>email</code> - User's email address</div>
                <div><code>password</code> - User's password (for welcome emails)</div>
                <div><code>role</code> - User's role</div>
                <div><code>company_id</code> - User's company ID</div>
              </div>
            </div>
            <div>
              <strong className="text-blue-800">System Variables:</strong>
              <div className="text-blue-700 mt-1 space-y-1">
                <div><code>current_date</code> - Current date</div>
                <div><code>current_time</code> - Current time</div>
                <div><code>system_url</code> - System URL</div>
                <div><code>support_email</code> - Support email</div>
              </div>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-2">
            Use variables in your content like: <code>Hello {'{first_name}'}</code>
          </p>
        </div>


      </div>
    </FormModal>
  );
};